import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Collapse, Descriptions, Dropdown, Menu, Popconfirm, Row, Space, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { RiArrowLeftLine, RiErrorWarningLine } from 'react-icons/ri';
import { PiPencilSimpleLine, PiTrashSimple } from 'react-icons/pi';
import { useIntl } from 'react-intl';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import urlPageTemplates from '../../../../../urls/urlPageTemplates';
import urlPageTemplatesVisualizations from '../../../../../urls/urlPageTemplatesVisualizations';
import urlPageTemplateDetails from '../../../../../urls/urlPageTemplateDetails';
import urlPageTemplatesVisualizationsEdit from '../../../../../urls/urlPageTemplatesVisualizationsEdit';
import { HasRights, PrivateComponent } from '../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../const/permissions';
import { DATE_TIME_FORMAT, EMPTY_VALUE_PLACEHOLDER } from '../../../../../const/system';
import getFormattedDate from '../../../../../lib/getFormattedDate';
import handleResponse from '../../../../../lib/handleResponse';
import useCheckMobileScreen from '../../../../../app/hooks/useCheckMobileScreen';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import Spinner from '../../../../../components/Spinner';
import CollapsibleText from '../../../../../components/CollapsibleText';
import HTMLCodePreview from '../../../components/HTMLCodePreview';
import HTMLPreview from '../../../components/HTMLPreview';
import { useDeleteTemplatesVisualizationMutation, useGetTemplatesVisualizationQuery } from '../../../api/templatesVisualizationsApiSlice';



const { Panel } = Collapse;
const { Title } = Typography;


const PageTemplatesVisualizationDetails = () => {
  const { visualizationId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const isMobile = useCheckMobileScreen(600);
  const canViewTemplate = HasRights([ Permissions.CONSTRUCTORS.TEMPLATES.FIND ]);

  const { data = { data: {} }, isFetching } = useGetTemplatesVisualizationQuery(visualizationId);
  const [ deleteTemplatesVisualization, { isLoading: isDeletingTemplatesVisualization } ] = useDeleteTemplatesVisualizationMutation();

  const visualizationData = data?.data ?? {};
  const isLoading = isFetching || isDeletingTemplatesVisualization;


  const handleDelete = () => {
    deleteTemplatesVisualization(visualizationId)
      .unwrap()
      .then(() => handleResponse(
        'success',
        intl,
        'ui-general-deleted-successfully',
        [ () => navigate(urlPageTemplatesVisualizations()) ],
      ))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const actionItems = (
    <Menu>
      <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.EDIT ]}>
        <Menu.Item
          key="edit"
          icon={<PiPencilSimpleLine size={18} />}
          onClick={() => navigate(urlPageTemplatesVisualizationsEdit({ visualizationId }))}
        >
          <IntlMessages id="ui-general-edit" />
        </Menu.Item>
      </PrivateComponent>
      <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.DELETE ]}>
        <Popconfirm
          title={<IntlMessages id="ui-general-delete-confirm-message" />}
          placement="topLeft"
          onConfirm={handleDelete}
          okText={<IntlMessages id="ui-general-yes" />}
          cancelText={<IntlMessages id="ui-general-no" />}
          icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
          okButtonProps={{ danger: true }}
        >
          <Menu.Item
            key="delete"
            icon={<PiTrashSimple size={18} />}
            onClick={() => {}}
          >
            <IntlMessages id="ui-general-delete" />
          </Menu.Item>
        </Popconfirm>
      </PrivateComponent>
    </Menu>
  );

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <Col span={24} md={18}>
          <BreadCrumbs
            breadCrumbParent={
              <Link to={urlPageTemplates()}>
                <IntlMessages id='templates-breadcrumbs' />
              </Link>
            }
            breadCrumbParent2={
              <Link to={urlPageTemplatesVisualizations()}>
                <IntlMessages id='templates-visualizations-breadcrumbs' />
              </Link>
            }
            breadCrumbActive={<CollapsibleText text={visualizationData?.title} />}
          />
        </Col>

        <Col md={6} span={24} className='text-end'>
          <Space size='middle'>
            <PrivateComponent
              allowedPermissions={[
                Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.EDIT,
                Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.DELETE,
              ]}
            >
              <Dropdown overlay={actionItems}>
                <Button onClick={(ev) => ev.preventDefault()}>
                  <Space size='middle'>
                    <IntlMessages id='ui-general-action' />
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            </PrivateComponent>
          </Space>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <Spinner spinning={isLoading}>
              <Row gutter={[ 32, 32 ]}>
                <Col xs={24} md={12} xl={12} xxl={16}>
                  <Descriptions
                    title={<IntlMessages id='templates-visualizations-breadcrumbs' />}
                    layout={isMobile ? 'vertical' : 'horizontal'}
                    bordered
                    column={1}
                  >
                    <Descriptions.Item
                      className='width-20-vw'
                      label={<IntlMessages id="ui-general-id" />}
                    >
                      {visualizationData.id}
                    </Descriptions.Item>
                    <Descriptions.Item label={<IntlMessages id="ui-general-title" />}>
                      <CollapsibleText text={visualizationData?.title} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<IntlMessages id="ui-general-template" />}>
                      {visualizationData?.template_id ? (
                        <div>
                          {canViewTemplate ? (
                            <Link to={urlPageTemplateDetails({ templateId: visualizationData?.template_id })} target="_blank">
                              {visualizationData?.template_name}
                            </Link>
                          ) : <div>{visualizationData?.template_name}</div>}
                        </div>
                      ) : EMPTY_VALUE_PLACEHOLDER}
                    </Descriptions.Item>
                    <Descriptions.Item label={<IntlMessages id="ui-general-description" />}>
                      <CollapsibleText text={visualizationData?.description || EMPTY_VALUE_PLACEHOLDER} />
                    </Descriptions.Item>
                    <Descriptions.Item label={<IntlMessages id="ui-general-created-at" />}>
                      {getFormattedDate(visualizationData?.created_at, DATE_TIME_FORMAT)}
                    </Descriptions.Item>
                    <Descriptions.Item label={<IntlMessages id="ui-general-updated-at" />}>
                      {getFormattedDate(visualizationData?.updated_at, DATE_TIME_FORMAT)}
                    </Descriptions.Item>
                  </Descriptions>

                  <Collapse className='hp-mt-30'>
                    <Panel
                      key="1"
                      header={
                        <Title level={5} className='collapse-item-title'>
                          <IntlMessages id="ui-general-html" />
                        </Title>
                      }
                    >
                      <HTMLCodePreview data={visualizationData?.html} />
                    </Panel>
                  </Collapse>
                </Col>

                <Col xs={24} md={12} xl={12} xxl={8} className='hp-pt-10'>
                  {visualizationData?.html && <HTMLPreview html={visualizationData.html} />}
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} justify='end' className='hp-mt-32'>
                <Col>
                  <Button
                    icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
                    onClick={() => navigate(urlPageTemplatesVisualizations())}
                  >
                    <IntlMessages id='ui-general-back-to-list' />
                  </Button>
                </Col>
              </Row>
            </Spinner>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageTemplatesVisualizationDetails;

